import { useEffect, useRef, useState } from "react";
import { Send, Sparkles } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { api } from "../lib/api";

const SUGGESTIONS = [
    "Show me upcoming Lagos artists with Asake-style energy",
    "Best Amapiano playlists for a Friday night",
    "Who are the rising female Afrobeats stars in 2026?",
    "Recommend Naija street-pop songs like Olamide",
];

export default function DiscoverPage() {
    const [sessionId] = useState(() => `sess-${Math.random().toString(36).slice(2)}`);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            text: "Welcome to MyStreetz AI Music Discovery. Ask me for upcoming artists, vibes, playlists, or sounds — I’ll point you to the heat.",
        },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" });
    }, [messages]);

    const send = async (text) => {
        const q = (text ?? input).trim();
        if (!q) return;
        setMessages((m) => [...m, { role: "user", text: q }]);
        setInput("");
        setLoading(true);
        try {
            const r = await api.post("/discovery/chat", {
                session_id: sessionId,
                message: q,
            });
            setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
        } catch {
            setMessages((m) => [
                ...m,
                { role: "assistant", text: "Couldn't reach the AI. Try again in a moment." },
            ]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-black text-white min-h-screen flex flex-col">
            <SEO
                title="AI Music Discovery"
                description="Find your next favourite Afrobeats artist, playlist or vibe — powered by MyStreetz AI."
                keywords={["AI music discovery", "Afrobeats AI", "music recommendations"]}
            />
            <Header />
            <main className="flex-1 max-w-4xl w-full mx-auto px-4 md:px-8 py-10 flex flex-col">
                <div className="overline text-streetz-yellow flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> AI MUSIC DISCOVERY
                </div>
                <h1 className="font-display uppercase text-4xl md:text-6xl leading-none mt-3">
                    Find your next sound
                </h1>

                <div
                    ref={scrollRef}
                    data-testid="discovery-chat-window"
                    className="flex-1 bg-streetz-paper border border-streetz-line p-4 md:p-6 mt-8 flex flex-col gap-4 overflow-y-auto min-h-[400px] max-h-[60vh]"
                >
                    {messages.map((m, i) => (
                        <div
                            key={`${m.role}-${i}`}
                            data-testid={`chat-${m.role}-${i}`}
                            className={`max-w-[85%] px-4 py-3 border ${
                                m.role === "user"
                                    ? "self-end bg-streetz-red border-streetz-red text-white"
                                    : "self-start bg-black border-streetz-line text-white"
                            }`}
                        >
                            <div className="overline mb-1 opacity-70">
                                {m.role === "user" ? "YOU" : "MYSTREETZ AI"}
                            </div>
                            <p className="text-sm leading-relaxed whitespace-pre-line">{m.text}</p>
                        </div>
                    ))}
                    {loading && (
                        <div className="self-start text-streetz-muted overline">
                            MyStreetz AI is typing…
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                    {SUGGESTIONS.map((s) => (
                        <button
                            key={s}
                            onClick={() => send(s)}
                            data-testid={`discovery-suggestion-${s.slice(0, 12).replace(/\s+/g, "-")}`}
                            className="overline text-xs border border-streetz-line px-3 py-2 text-white hover:bg-streetz-yellow hover:text-black hover:border-streetz-yellow"
                        >
                            {s}
                        </button>
                    ))}
                </div>

                <form
                    onSubmit={(e) => { e.preventDefault(); send(); }}
                    className="mt-4 flex gap-2"
                >
                    <input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Ask about artists, vibes, playlists…"
                        data-testid="discovery-input"
                        className="flex-1 bg-black border border-streetz-line px-4 py-3 text-white focus:border-streetz-red outline-none"
                    />
                    <button
                        disabled={loading}
                        type="submit"
                        data-testid="discovery-send-button"
                        className="bg-streetz-red text-white px-5 inline-flex items-center gap-2 overline hover:bg-white hover:text-black transition-colors disabled:opacity-60"
                    >
                        SEND <Send className="w-4 h-4" />
                    </button>
                </form>
            </main>
            <Footer />
        </div>
    );
}
